<template>
  <UModal
    :value="value"
    width="760px"
    close
    lock
    type="test-create-modal"
    @input="$emit('input', $event)"
  >
    <div class="test-create">
      <div class="test-create__heading">
        <button
          v-if="step !== 'choice'"
          type="button"
          class="test-create__back"
          @click="step = 'choice'"
        >
          <feather-icon icon="ArrowLeftIcon" size="20" />
        </button>
        <div>
          <h2>{{ heading }}</h2>
          <p>{{ subtitle }}</p>
        </div>
      </div>

      <div v-if="step === 'choice'" class="test-create__choices">
        <button type="button" class="source-card" @click="step = 'file'">
          <span class="source-card__icon source-card__icon--blue">
            <feather-icon icon="FileTextIcon" size="26" />
          </span>
          <span>
            <strong>Создать из файла</strong>
            <small>Загрузите PDF или DOCX — YandexGPT подготовит вопросы</small>
          </span>
          <feather-icon icon="ChevronRightIcon" size="20" />
        </button>

        <button type="button" class="source-card" @click="step = 'material'">
          <span class="source-card__icon source-card__icon--violet">
            <feather-icon icon="BookOpenIcon" size="26" />
          </span>
          <span>
            <strong>Создать из материала</strong>
            <small>Выберите готовый материал из вашей базы</small>
          </span>
          <feather-icon icon="ChevronRightIcon" size="20" />
        </button>

        <button type="button" class="source-card source-card--manual" @click="$emit('manual')">
          <span class="source-card__icon">
            <feather-icon icon="Edit3Icon" size="26" />
          </span>
          <span>
            <strong>Создать вручную</strong>
            <small>Откройте пустой редактор теста</small>
          </span>
          <feather-icon icon="ChevronRightIcon" size="20" />
        </button>
      </div>

      <template v-else>
        <div v-if="step === 'file'" class="test-create__file">
          <label class="file-drop" :class="{ 'file-drop--selected': file }">
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              @change="onFileChange"
            />
            <feather-icon :icon="file ? 'CheckCircleIcon' : 'UploadCloudIcon'" size="34" />
            <strong>{{ file ? file.name : 'Выберите PDF или DOCX' }}</strong>
            <small>{{ file ? fileSize : 'Максимальный размер файла — 15 МБ' }}</small>
          </label>
        </div>

        <div v-else class="test-create__materials">
          <div class="material-search">
            <feather-icon icon="SearchIcon" size="18" />
            <input v-model.trim="search" type="search" placeholder="Поиск по материалам" />
          </div>

          <div v-if="filteredMaterials.length" class="material-list custom-scrollbar">
            <button
              v-for="material of filteredMaterials"
              :key="material.id"
              type="button"
              class="material-option"
              :class="{ 'material-option--active': selectedMaterialId === material.id }"
              @click="selectedMaterialId = material.id"
            >
              <span class="material-option__check">
                <feather-icon v-if="selectedMaterialId === material.id" icon="CheckIcon" size="16" />
              </span>
              <span>
                <strong>{{ material.title || 'Без названия' }}</strong>
                <small>{{ material.description || 'Без описания' }}</small>
              </span>
            </button>
          </div>
          <div v-else class="material-empty">
            {{ search ? 'По вашему запросу ничего не найдено' : 'Материалов пока нет' }}
          </div>
        </div>

        <div class="generation-settings">
          <label class="generation-settings__count">
            <span>Количество вопросов</span>
            <input v-model.number="questionCount" type="number" min="1" max="30" />
          </label>

          <div class="generation-settings__types">
            <span>Типы вопросов</span>
            <label v-for="type of questionTypeOptions" :key="type.value">
              <input v-model="questionTypes" type="checkbox" :value="type.value" />
              <span>{{ type.title }}</span>
            </label>
          </div>
        </div>

        <div class="test-create__actions">
          <UButton variant="gray" :disabled="loading" @click="step = 'choice'">
            Назад
          </UButton>
          <UButton :loading="loading" :disabled="loading" @click="generate">
            {{ loading ? 'Создаем вопросы…' : 'Создать тест' }}
          </UButton>
        </div>
      </template>
    </div>
  </UModal>
</template>

<script>
import { coreApi } from '@/api'
import UButton from '@/components/redesign/ui/UButton.vue'
import UModal from '@/components/redesign/ui/UModal.vue'

export default {
  name: 'TestCreateModal',
  components: { UButton, UModal },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    materials: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      step: 'choice',
      file: null,
      search: '',
      selectedMaterialId: null,
      questionCount: 10,
      questionTypes: ['default'],
      questionTypeOptions: [
        { value: 'default', title: 'Один правильный ответ' },
        { value: 'multiply', title: 'Несколько правильных ответов' },
        { value: 'free', title: 'Ответ текстом' },
      ],
      loading: false,
    }
  },
  computed: {
    heading() {
      if (this.step === 'file') return 'Создание из файла'
      if (this.step === 'material') return 'Создание из материала'
      return 'Создать тест'
    },
    subtitle() {
      if (this.step === 'file') return 'Загрузите источник и настройте будущий тест'
      if (this.step === 'material') return 'Найдите материал и выберите его в списке'
      return 'Выберите, на основе чего подготовить вопросы'
    },
    filteredMaterials() {
      const query = this.search.toLocaleLowerCase('ru-RU')
      if (!query) return this.materials

      return this.materials.filter(material => [material.title, material.description]
        .filter(Boolean)
        .some(value => String(value).toLocaleLowerCase('ru-RU').includes(query)))
    },
    selectedMaterial() {
      return this.materials.find(material => material.id === this.selectedMaterialId)
    },
    fileSize() {
      if (!this.file) return ''
      const sizeMb = this.file.size / 1024 / 1024
      return `${sizeMb.toFixed(sizeMb >= 1 ? 1 : 2)} МБ`
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.reset()
    },
  },
  methods: {
    reset() {
      this.step = 'choice'
      this.file = null
      this.search = ''
      this.selectedMaterialId = null
      this.questionCount = 10
      this.questionTypes = ['default']
      this.loading = false
    },
    onFileChange(event) {
      const input = event.target
      const [file] = input.files || []
      if (!file) {
        this.file = null
        return
      }

      const extension = file.name.split('.').pop().toLowerCase()
      if (!['pdf', 'docx'].includes(extension)) {
        this.$toast.error('Поддерживаются только PDF и DOCX')
        input.value = ''
        return
      }

      if (file.size > 15 * 1024 * 1024) {
        this.$toast.error('Максимальный размер файла — 15 МБ')
        input.value = ''
        return
      }

      this.file = file
    },
    validate() {
      if (this.step === 'file' && !this.file) {
        this.$toast.error('Выберите PDF или DOCX')
        return false
      }

      if (this.step === 'material' && !this.selectedMaterial) {
        this.$toast.error('Выберите материал')
        return false
      }

      if (!this.questionTypes.length) {
        this.$toast.error('Выберите хотя бы один тип вопроса')
        return false
      }

      if (this.questionCount < 1 || this.questionCount > 30) {
        this.$toast.error('Укажите от 1 до 30 вопросов')
        return false
      }

      return true
    },
    async generate() {
      if (!this.validate() || this.loading) return

      this.loading = true
      try {
        const common = {
          userId: this.user.id,
          questionCount: Number(this.questionCount),
          questionTypes: this.questionTypes,
        }
        const result = this.step === 'file'
          ? await coreApi.generateTestFromFile({ ...common, file: this.file })
          : await coreApi.generateTestFromMaterial({
            ...common,
            materialId: this.selectedMaterial.id,
          })

        if (!result?.ok) {
          throw new Error(result?.message || 'Не удалось создать вопросы')
        }

        const sourceTitle = this.step === 'file'
          ? this.file.name.replace(/\.(pdf|docx)$/i, '')
          : this.selectedMaterial.title
        const sourceDescription = this.step === 'material'
          ? this.selectedMaterial.description
          : `Тест создан на основе файла «${this.file.name}»`

        this.$emit('generated', {
          test: {
            title: `Тест: ${sourceTitle}`,
            description: sourceDescription || '',
            attempts: 1,
            rightAnswer: result.questions.length,
          },
          questions: result.questions,
        })
      } catch (error) {
        const message = error?.response?.data?.message || error.message || 'Не удалось создать вопросы'
        this.$toast.error(message)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
.test-create-modal .modal-wrapper {
  padding: 28px 32px 30px;
}

.test-create {
  color: #2D3748;

  &__heading {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 26px;
    padding-right: 42px;

    h2 {
      margin: 0 0 6px;
      font-size: 25px;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: #8A94A4;
      font-size: 14px;
    }
  }

  &__back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    color: #0088CC;
    background: #E6F3FA;
    border: 0;
    border-radius: 10px;
  }

  &__choices {
    display: grid;
    gap: 12px;
  }

  .source-card {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr) 20px;
    gap: 16px;
    align-items: center;
    width: 100%;
    padding: 16px;
    color: #2D3748;
    text-align: left;
    background: #FFFFFF;
    border: 1px solid #E4E9F0;
    border-radius: 14px;
    transition: 0.2s ease;

    &:hover {
      border-color: #8FCDEA;
      box-shadow: 0 8px 24px rgba(0, 136, 204, 0.09);
      transform: translateY(-1px);
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 52px;
      height: 52px;
      color: #667085;
      background: #F1F3F6;
      border-radius: 12px;

      &--blue {
        color: #0088CC;
        background: #E6F3FA;
      }

      &--violet {
        color: #7656C8;
        background: #F0EBFC;
      }
    }

    strong,
    small {
      display: block;
    }

    strong {
      margin-bottom: 4px;
      font-size: 16px;
    }

    small {
      color: #8A94A4;
      font-size: 13px;
      line-height: 18px;
    }
  }

  .file-drop {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    min-height: 190px;
    padding: 24px;
    color: #0088CC;
    text-align: center;
    background: #F7FBFD;
    border: 2px dashed #A8D8EE;
    border-radius: 16px;
    cursor: pointer;

    &--selected {
      color: #228B62;
      background: #F2FBF7;
      border-color: #8FD5B7;
    }

    input {
      display: none;
    }

    strong {
      margin: 12px 0 5px;
      color: #344054;
      font-size: 16px;
    }

    small {
      color: #8A94A4;
    }
  }

  .material-search {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 14px;
    color: #98A2B3;
    background: #F7F8FA;
    border: 1px solid #E4E7EC;
    border-radius: 10px;

    input {
      width: 100%;
      height: 100%;
      padding: 0;
      border: 0;
      outline: 0;
      background: transparent;
    }
  }

  .material-list {
    display: grid;
    gap: 8px;
    max-height: 260px;
    margin-top: 12px;
    overflow: auto;
  }

  .material-option {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 12px;
    width: 100%;
    padding: 13px 14px;
    color: #344054;
    text-align: left;
    background: #FFFFFF;
    border: 1px solid #E4E7EC;
    border-radius: 10px;

    &--active {
      background: #F3FAFD;
      border-color: #0088CC;
    }

    &__check {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      color: #FFFFFF;
      background: #FFFFFF;
      border: 1px solid #CBD5E1;
      border-radius: 50%;
    }

    &--active &__check {
      background: #0088CC;
      border-color: #0088CC;
    }

    strong,
    small {
      display: block;
    }

    small {
      margin-top: 3px;
      color: #8A94A4;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .material-empty {
    padding: 44px 16px;
    color: #98A2B3;
    text-align: center;
  }

  .generation-settings {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 24px;
    margin-top: 22px;
    padding: 18px;
    background: #F8FAFC;
    border-radius: 12px;

    &__count,
    &__types {
      & > span {
        display: block;
        margin-bottom: 9px;
        color: #667085;
        font-size: 13px;
        font-weight: 500;
      }
    }

    &__count input {
      width: 100%;
      height: 42px;
      padding: 0 12px;
      background: #FFFFFF;
      border: 1px solid #DDE3EA;
      border-radius: 8px;
    }

    &__types {
      display: flex;
      flex-wrap: wrap;
      align-content: flex-start;
      gap: 8px 16px;

      & > span {
        flex-basis: 100%;
      }

      label {
        display: flex;
        align-items: center;
        gap: 7px;
        margin: 0;
        font-size: 13px;
      }
    }
  }

  &__actions {
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr);
    gap: 12px;
    margin-top: 22px;

    .button {
      height: 48px;
    }
  }

  @media (max-width: 576px) {
    .generation-settings,
    &__actions {
      grid-template-columns: 1fr;
    }

    .source-card {
      grid-template-columns: 44px minmax(0, 1fr);

      & > svg {
        display: none;
      }

      &__icon {
        width: 44px;
        height: 44px;
      }
    }
  }
}
</style>
